// src/DevStreamControls.js
import React, { useState } from "react";
import "./DevStream.css";
import logger from "../../utils/logger";

/**
 * DevStreamControls - Control buttons for dev streams
 *
 * Features:
 * - YP/MC source toggles
 * - Copy single event (via event card)
 * - Copy all events
 * - Clear all events
 * - Test event generation (1 or 10 events with optional custom payload)
 */
const DevStreamControls = ({
  ypEnabled,
  mcEnabled,
  toggleYP,
  toggleMC,
  clearEvents,
  sendTestEvent,
  authenticated,
  eventCount,
  maxEvents,
  updateMaxEvents,
}) => {
  const [showTestOptions, setShowTestOptions] = useState(false);
  const [customPayload, setCustomPayload] = useState("");

  const handleMaxEventsChange = (e) => {
    const value = e.target.value;
    // Only allow numbers
    if (value === "" || /^\d+$/.test(value)) {
      updateMaxEvents(value === "" ? "1" : value);
    }
  };

  const handleCopyAll = () => {
    if (eventCount === 0) return;

    // Get all events from the view and extract raw event bodies
    const eventCards = document.querySelectorAll(".dev-stream-event-card");
    const allRawEventBodies = Array.from(eventCards)
      .map((card) => {
        const eventData = card.getAttribute("data-event");
        if (!eventData) return null;

        const event = JSON.parse(eventData);
        const data = event?.data;

        if (!data) return null;

        // Extract source to determine how to parse
        const source =
          data?._devStreamMetadata?.source || data?.source || "UNKNOWN";

        // For MC events: extract just the payload
        if (source === "MC" && data.payload) {
          return data.payload;
        }

        // For YP events (SNS): extract raw SNS message without our wrapper fields
        const { source: src, timestamp, _devStreamMetadata, ...rawSNS } = data;
        return rawSNS;
      })
      .filter(Boolean);

    const text = JSON.stringify(allRawEventBodies, null, 2);
    navigator.clipboard
      .writeText(text)
      .then(() => {
        // Visual feedback
        const btn = document.querySelector(".copy-all-btn");
        if (btn) {
          const originalText = btn.textContent;
          btn.textContent = "✓ Copied!";
          setTimeout(() => {
            btn.textContent = originalText;
          }, 2000);
        }
      })
      .catch((err) => {
        logger.error("Failed to copy events:", err);
      });
  };

  const handleTestEvent = (count) => {
    let payload = null;

    if (customPayload.trim()) {
      try {
        payload = JSON.parse(customPayload);
      } catch (e) {
        alert(
          "Invalid JSON in custom payload. Sending without custom payload."
        );
      }
    }

    sendTestEvent(count, payload);

    // Close test options after sending
    setShowTestOptions(false);
    setCustomPayload("");
  };

  return (
    <div className="dev-stream-controls">
      {/* Source Toggles */}
      <div className="source-toggles">
        <button
          className={`source-toggle ${ypEnabled ? "active" : ""}`}
          onClick={toggleYP}
          title="Toggle YP events"
        >
          <span className="toggle-icon">{ypEnabled ? "✓" : "○"}</span>
          Yonomi Platform Events
        </button>

        <button
          className={`source-toggle source-toggle-mc ${
            mcEnabled ? "active" : ""
          }`}
          onClick={toggleMC}
          title="Toggle MC events"
        >
          <span className="toggle-icon">{mcEnabled ? "✓" : "○"}</span>
          Mobile Credentials Events
        </button>
      </div>

      {/* Max Events Configuration */}
      <div className="max-events-config">
        <label htmlFor="max-events-input" className="max-events-label">
          # of events to capture (Max 300):
        </label>
        <input
          id="max-events-input"
          type="text"
          inputMode="numeric"
          pattern="[0-9]*"
          className="max-events-input"
          value={maxEvents}
          onChange={handleMaxEventsChange}
          title="Set maximum number of events (1-300)"
        />
      </div>

      {/* Action Buttons */}
      <div className="control-actions">
        <button
          className="control-btn copy-all-btn"
          onClick={handleCopyAll}
          disabled={eventCount === 0}
          title="Copy all events to clipboard as JSON"
        >
          📋 Copy All ({eventCount})
        </button>

        <button
          className="control-btn clear-btn"
          onClick={clearEvents}
          disabled={eventCount === 0}
          title="Clear all events"
        >
          🗑️ Clear
        </button>

        <button
          className="control-btn test-btn"
          onClick={() => setShowTestOptions(!showTestOptions)}
          disabled={!authenticated}
          title={
            authenticated
              ? "Generate test events"
              : "Authenticate to send test events"
          }
        >
          🧪 Test Events {showTestOptions ? "▼" : "▶"}
        </button>
      </div>

      {/* Test Event Options */}
      {showTestOptions && authenticated && (
        <div className="test-event-options">
          <div className="test-event-buttons">
            <button
              className="test-event-btn"
              onClick={() => handleTestEvent(1)}
            >
              Send 1 Event
            </button>

            <button
              className="test-event-btn"
              onClick={() => handleTestEvent(10)}
            >
              Send 10 Events
            </button>
          </div>

          <div className="custom-payload-section">
            <label>Custom Payload (optional JSON):</label>
            <textarea
              className="custom-payload-input"
              placeholder='{"deviceId": "test-123", "customField": "value"}'
              value={customPayload}
              onChange={(e) => setCustomPayload(e.target.value)}
              rows={3}
            />
            <small className="input-hint">
              Leave empty for auto-generated test events. Must be valid JSON.
            </small>
          </div>
        </div>
      )}
    </div>
  );
};

export default DevStreamControls;
